import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAgents } from '@/lib/api/agents';

const PAGE_SIZE = 20;

export function AgentsListPage() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const agentsQuery = useAgents({
    page,
    pageSize: PAGE_SIZE,
    search: search.trim() || undefined,
  });

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const result = agentsQuery.data;
  const pageCount = result ? Math.max(1, Math.ceil(result.total / result.pageSize)) : 1;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Agents</h1>
          <p className="text-sm text-[var(--color-muted-foreground)]">
            Conversational agents and their versioned configuration.
          </p>
        </div>
        <Button asChild>
          <Link to="/agents/new">
            <Plus className="size-4" />
            New agent
          </Link>
        </Button>
      </div>

      <Input
        placeholder="Search by name or slug…"
        value={search}
        onChange={(event) => onSearch(event.target.value)}
        className="h-8 w-full sm:w-64"
      />

      {agentsQuery.isPending && <p className="text-sm">Loading…</p>}
      {agentsQuery.isError && (
        <p className="text-sm text-[var(--color-destructive)]">
          Failed to load agents: {agentsQuery.error.message}
        </p>
      )}

      {result && (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Model tier</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Updated by</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {result.items.length ? (
                  result.items.map((agent) => (
                    <TableRow key={agent.id}>
                      <TableCell>
                        <Link
                          to="/agents/$agentId"
                          params={{ agentId: agent.id }}
                          className="font-medium hover:underline"
                        >
                          {agent.activeVersion.name}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{agent.activeVersion.slug}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{agent.activeVersion.modelTier}</TableCell>
                      <TableCell className="text-sm">
                        v{agent.activeVersion.versionNumber}
                      </TableCell>
                      <TableCell className="text-sm">
                        {agent.activeVersion.updatedByEmail}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="h-24 text-center">
                      No agents yet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-[var(--color-muted-foreground)]">{result.total} agent(s)</p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                Previous
              </Button>
              <span className="text-sm text-[var(--color-muted-foreground)]">
                Page {page} of {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((value) => value + 1)}
                disabled={page >= pageCount}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
